import { track } from "@/desktop/observability";
import {
  interactionCueDuration,
  PEN_TOUCH_DOWN_MS,
} from "./presentation-interaction-timing";
import {
  animateHighlight,
  highlightDrawingDuration,
} from "@/lib/presentation-highlight-animation";
import {
  cursorContentForStep,
  annotationCalloutForStep,
  travelDurationForStep,
  showDraw,
  showDragPreview,
} from "@/lib/presentation-drawing-operations";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { abortableDelay } from "@/lib/abortable-delay";
import { ensureFrameTargetsVisible } from "@/lib/presentation-frame-targets";
import {
  acknowledgePresentationPaint,
  failPresentationStep,
} from "@/lib/agent-presentation-ack";
import {
  resolvePresentationExclusions,
  resolvePresentationFrameDisplayId,
  resolvePresentationFramePoint,
  resolvePresentationPoint,
  resolvePresentationRect,
} from "@/lib/agent-presentation-contract";
import type {
  AgentPresentation,
  AgentPresentationStep,
} from "@/lib/agent-presentation-types";
import {
  presentAnnotationPointer,
  presentCursor,
  type PresentAnnotation,
} from "@/lib/canvas-cursor-presentation";
import {
  narratePresentationStep,
  preparePresentationNarration,
} from "@/lib/presentation-narration";
import { performPresentationInteraction } from "@/lib/agent-presentation-interaction";
import { waitForCursorAnimation } from "@/lib/cursor-animation-completion";
import { agentCursorAnnotationTargets } from "@/lib/agent-annotation";
import { presentFramesOrQueue } from "@/lib/pending-frame-presentation";
import { useCanvasStore } from "@/stores/canvas-store";
import {
  clearPresentationAnnotations,
  clearPresentationFrames,
  clearPresentationVisuals,
  finishPresentationVisuals,
  followPresentationVisuals,
} from "@/lib/agent-presentation-cleanup";
export interface PresentationOperations {
  showCursor: (
    presentation: AgentPresentation,
    step: AgentPresentationStep,
    actionSeq: number,
    signal: AbortSignal,
  ) => boolean | Promise<boolean>;
  showFrames: (
    presentation: AgentPresentation,
    step: Extract<AgentPresentationStep, { kind: "frame" }>,
    signal: AbortSignal,
  ) => Promise<boolean>;
  prepare?: (
    presentation: AgentPresentation,
    step: AgentPresentationStep,
    signal: AbortSignal,
  ) => void;
  narrate?: (
    presentation: AgentPresentation,
    step: AgentPresentationStep,
    signal: AbortSignal,
  ) => Promise<boolean>;
  interact: (
    presentation: AgentPresentation,
    step: Extract<
      AgentPresentationStep,
      { kind: "click" | "hover" | "drag" | "scroll" | "paste" | "key" | "type" }
    >,
    signal: AbortSignal,
    onFailure?: (reason: string) => void,
  ) => Promise<boolean>;
  clearVisuals: (
    presentation: AgentPresentation,
    scope?: "presentation" | "execution",
    /** Every step finished: a replay of this presentation is ignored. */
    completed?: boolean,
  ) => void;
  finishVisuals: (presentation: AgentPresentation) => void;
  /** The view moved: painted visuals follow `presentation.projectId`. */
  followVisuals?: (presentation: AgentPresentation) => void;
  clearAnnotations: (
    presentation: AgentPresentation,
    scope?: "presentation" | "execution",
  ) => void;
  clearFrames: (presentation: AgentPresentation) => void;
  delay: (durationMs: number, signal: AbortSignal) => Promise<void>;
}
function annotationForStep(
  step: Extract<AgentPresentationStep, { kind: "highlight" }>,
  rect: NonNullable<ReturnType<typeof resolvePresentationRect>>,
): PresentAnnotation | null {
  const common = {
    ...rect,
    emphasis: step.emphasis,
    strokeWidth: step.stroke_width,
    fillOpacity: step.fill_opacity,
    padding: step.padding,
    transition: step.transition?.style,
    transitionDurationMs: step.transition?.duration_ms,
  };
  if (step.shape === "path" || step.shape === "polyline") {
    return step.path ? { ...common, shape: step.shape, path: step.path } : null;
  }
  return { ...common, shape: step.shape };
}

async function defaultShowCursor(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
  actionSeq: number,
  signal: AbortSignal,
): Promise<boolean> {
  if (step.kind === "draw") {
    return showDraw(presentation, step, actionSeq, signal);
  }
  if (step.kind === "drag") {
    return showDragPreview(presentation, step, actionSeq, signal);
  }
  const revealing = ensureFrameTargetsVisible(step, signal);
  if (revealing) {
    await revealing;
    if (signal.aborted) return false;
  }
  if (
    step.kind !== "point" &&
    step.kind !== "highlight" &&
    step.kind !== "click" &&
    step.kind !== "hover" &&
    step.kind !== "paste" &&
    step.kind !== "scroll" &&
    step.kind !== "key"
  ) {
    return false;
  }
  const cursorPoint =
    step.kind === "point" ||
    step.kind === "click" ||
    step.kind === "hover" ||
    step.kind === "paste" ||
    step.kind === "scroll" ||
    step.kind === "key"
      ? resolvePresentationPoint(step, presentation)
      : undefined;
  const resolvedAnnotation =
    step.kind === "highlight"
      ? resolvePresentationRect(step, presentation)
      : undefined;
  if (!cursorPoint && !resolvedAnnotation) {
    await failPresentationStep(
      presentation,
      step,
      "target_geometry_unresolved",
    );
    return false;
  }
  const travelDurationMs = travelDurationForStep(step);
  const completionToken = `${presentation.presentationId}:${step.step_id}:${actionSeq}`;
  const annotation =
    step.kind === "highlight" && resolvedAnnotation
      ? annotationForStep(step, resolvedAnnotation)
      : undefined;
  if (step.kind === "highlight" && !annotation) {
    await failPresentationStep(
      presentation,
      step,
      "annotation_geometry_invalid",
    );
    return false;
  }
  const annotationId = `${presentation.presentationId}:${step.step_id}`;
  const highlightDurationMs =
    annotation && step.kind === "highlight"
      ? highlightDrawingDuration(annotation, step, annotationId)
      : 0;
  const touchDownMs = highlightDurationMs > 0 ? PEN_TOUCH_DOWN_MS : 0;
  const targetDisplayId =
    cursorPoint?.displayId ?? resolvedAnnotation?.displayId;
  const windowDisplayId = getWindowDisplayId();
  if (
    targetDisplayId != null &&
    windowDisplayId != null &&
    targetDisplayId !== windowDisplayId
  ) {
    await abortableDelay(
      travelDurationMs +
        touchDownMs +
        highlightDurationMs +
        (interactionCueDuration(step, presentation.mode) ?? 0),
      signal,
    );
    return !signal.aborted;
  }
  const exclusions = resolvePresentationExclusions(
    "exclusion_regions" in step ? step.exclusion_regions : undefined,
    presentation,
    targetDisplayId ?? undefined,
  );
  const annotationAnchor = annotation
    ? presentAnnotationPointer(annotation, annotationId)
    : undefined;
  const request = {
    cursorPoint: cursorPoint ?? annotationAnchor,
    cursorLabel: cursorContentForStep(step),
    agentId: presentation.agentId,
    agentName: presentation.agentName,
    agentColor: presentation.agentColor,
    projectId: presentation.projectId,
    spaceId: presentation.spaceId,
    actionSeq,
    travelDurationMs,
    completionToken,
    presentationDeliveryId: presentation.presentationId,
    executionId: presentation.executionId,
    annotationCalloutRole:
      step.kind === "highlight" ? step.callout_role : undefined,
    annotationExclusionRects: exclusions.map((rect) => ({
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
      displayId: rect.displayId,
    })),
  };
  if (!presentCursor(request)) {
    await failPresentationStep(presentation, step, "cursor_space_unavailable");
    return false;
  }
  if (interactionCueDuration(step, presentation.mode) === null) {
    await waitForCursorAnimation(
      completionToken,
      signal,
      travelDurationMs + 250,
    );
  }
  if (touchDownMs > 0) await abortableDelay(touchDownMs, signal);
  if (signal.aborted) return false;
  if (annotation && highlightDurationMs > 0) {
    const completed = await animateHighlight(
      {
        ...request,
        annotationId,
        annotationCallout: annotationCalloutForStep(step),
      },
      annotation,
      highlightDurationMs,
      signal,
    );
    if (!completed || signal.aborted) return false;
  } else if (annotation) {
    presentCursor({
      ...request,
      annotation,
      annotationId,
      annotationCallout: annotationCalloutForStep(step),
      actionSeq: actionSeq * 10_000 + 1,
      travelDurationMs: 0,
      completionToken: undefined,
    });
  }
  const paintStarted = performance.now();
  if (!(await acknowledgePresentationPaint(presentation, step, signal)))
    return false;
  const cueDurationMs = interactionCueDuration(step, presentation.mode);
  if (cueDurationMs !== null) {
    track("desktop_interaction_phase", {
      phase: "presentation_painted",
      duration_ms: performance.now() - paintStarted,
      cue_duration_ms: cueDurationMs,
      action: step.kind,
    });
    // Instant/reduced-motion cues still cross the paint acknowledgement above.
    // Keep the warning interval abortable so Stop prevents input dispatch.
    await abortableDelay(cueDurationMs, signal);
  }
  return !signal.aborted;
}

async function defaultShowFrames(
  presentation: AgentPresentation,
  step: Extract<AgentPresentationStep, { kind: "frame" }>,
  signal: AbortSignal,
): Promise<boolean> {
  const cursorPoint = resolvePresentationFramePoint(step, presentation);
  const targetDisplayId = resolvePresentationFrameDisplayId(step, presentation);
  if (targetDisplayId == null) return true;
  if (cursorContentForStep(step) && !cursorPoint) {
    await failPresentationStep(
      presentation,
      step,
      "frame_bubble_anchor_unresolved",
    );
    return false;
  }
  const exclusions = resolvePresentationExclusions(
    step.exclusion_regions,
    presentation,
    targetDisplayId ?? undefined,
  );
  const agentCursor = presentation.agentId
    ? useCanvasStore.getState().agentCursors.get(presentation.agentId)
    : undefined;
  const annotationExclusion = agentCursorAnnotationTargets(agentCursor)
    .filter((annotation) => annotation.displayId === targetDisplayId)
    .map((annotation) => {
      const padding = Math.max(0, annotation.padding ?? 0);
      return {
        x: annotation.globalX - padding,
        y: annotation.globalY - padding,
        width: annotation.width + padding * 2,
        height: annotation.height + padding * 2,
        displayId: annotation.displayId,
      };
    });
  const presented = await presentFramesOrQueue(
    {
      frameIds: step.frame_ids,
      context: step.context ?? "canvas",
      placement: step.placement ?? "center",
      visibility: step.visibility,
      replacesRound: step.visibility === "show_only",
      cursorPoint,
      targetDisplayId,
      cursorLabel: cursorContentForStep(step),
      agentId: presentation.agentId,
      agentName: presentation.agentName,
      agentColor: presentation.agentColor,
      spaceId:
        presentation.projectId ??
        presentation.canvasSpaceId ??
        presentation.spaceId,
      executionId: presentation.executionId,
      ownerId: presentation.ownerId,
      abortSignal: signal,
      presentationDeliveryId: presentation.presentationId,
      desiredContentSize: step.desired_size
        ? {
            width: step.desired_size.width,
            height: step.desired_size.height,
            aspectRatio: step.desired_size.aspect_ratio,
          }
        : undefined,
      frameExclusionRects: [
        ...exclusions.map((rect) => ({
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height,
          displayId: rect.displayId,
        })),
        ...annotationExclusion,
      ],
    },
    signal,
  );
  if (!presented || signal.aborted) {
    if (!signal.aborted) {
      await failPresentationStep(
        presentation,
        step,
        "frame_presentation_expired",
      );
    }
    return false;
  }
  return acknowledgePresentationPaint(presentation, step, signal);
}

export const defaultPresentationOperations: PresentationOperations = {
  showCursor: defaultShowCursor,
  showFrames: defaultShowFrames,
  narrate: narratePresentationStep,
  prepare: preparePresentationNarration,
  interact: performPresentationInteraction,
  clearVisuals: clearPresentationVisuals,
  finishVisuals: finishPresentationVisuals,
  followVisuals: followPresentationVisuals,
  clearAnnotations: clearPresentationAnnotations,
  clearFrames: clearPresentationFrames,
  delay: abortableDelay,
};
